import { useEffect, useRef, useCallback, createElement } from "react";
import { useLocation } from "wouter";
import { toast } from "@/hooks/use-toast";
import { ToastAction, type ToastActionElement } from "@/components/ui/toast";

const TIMEOUT_MS = 30 * 60 * 1000;
const WARN_BEFORE_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 1000;

const ACTIVITY_KEY = "bazaar_activity_ts";
const SIGNOUT_KEY = "bazaar_inactivity_signout";
const AUTH_CHANNEL = "bazaar_auth";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "touchstart"] as const;

/**
 * Automatically signs the user out after TIMEOUT_MS of inactivity.
 * Shows a warning toast 5 min before expiry; dismissing it resets the timer.
 * Activity in any tab resets the timer across all tabs via localStorage events.
 * Logout is broadcast to other tabs via BroadcastChannel; localStorage is a fallback.
 */
export function useInactivityLogout(
  isAuthenticated: boolean,
  onSignOut: () => Promise<void>,
) {
  const [, navigate] = useLocation();
  const lastActivityRef = useRef(Date.now());
  const warnShownRef = useRef(false);
  const dismissWarnRef = useRef<(() => void) | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (warnShownRef.current) {
      warnShownRef.current = false;
      dismissWarnRef.current?.();
      dismissWarnRef.current = null;
    }
    try {
      localStorage.setItem(ACTIVITY_KEY, String(now));
    } catch {
      // storage may be unavailable (private browsing edge cases)
    }
  }, []);

  const executeSignOut = useCallback(async () => {
    // Primary: broadcast via BroadcastChannel (same-browser, ~1 ms)
    channelRef.current?.postMessage({ type: "logout" });
    // Fallback: localStorage storage event for browsers without BroadcastChannel
    try {
      localStorage.setItem(SIGNOUT_KEY, String(Date.now()));
    } catch {
      // best-effort
    }
    await onSignOut();
    navigate("/login?reason=inactivity");
  }, [onSignOut, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Open BroadcastChannel for this hook instance
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channelRef.current = channel;

    resetActivity();

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, resetActivity, { passive: true }),
    );

    function handleStorage(e: StorageEvent) {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        const ts = Number(e.newValue);
        if (ts > lastActivityRef.current) {
          lastActivityRef.current = ts;
          if (warnShownRef.current) {
            warnShownRef.current = false;
            dismissWarnRef.current?.();
            dismissWarnRef.current = null;
          }
        }
      }
      // Fallback: honour legacy localStorage signout signal
      if (e.key === SIGNOUT_KEY && e.newValue) {
        onSignOut().then(() => navigate("/login?reason=inactivity"));
      }
    }
    window.addEventListener("storage", handleStorage);

    const interval = setInterval(async () => {
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= TIMEOUT_MS) {
        clearInterval(interval);
        dismissWarnRef.current?.();
        dismissWarnRef.current = null;
        warnShownRef.current = false;
        await executeSignOut();
        return;
      }

      if (idle >= TIMEOUT_MS - WARN_BEFORE_MS && !warnShownRef.current) {
        warnShownRef.current = true;
        const minsLeft = Math.ceil((TIMEOUT_MS - idle) / 60000);
        const { dismiss } = toast({
          title: "Still there?",
          description: `You'll be signed out in ${minsLeft} minute${minsLeft !== 1 ? "s" : ""} due to inactivity.`,
          duration: Infinity,
          action: createElement(
            ToastAction,
            {
              altText: "Stay signed in",
              onClick: resetActivity,
            },
            "Stay signed in",
          ) as unknown as ToastActionElement,
        });
        dismissWarnRef.current = dismiss;
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, resetActivity),
      );
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
      dismissWarnRef.current?.();
      dismissWarnRef.current = null;
      channel.close();
      channelRef.current = null;
    };
  }, [isAuthenticated, resetActivity, executeSignOut, navigate, onSignOut]);
}
